// src/pages/MicrobeScene.tsx
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/Map.css';

const BASE_WIDTH = 640;
const BASE_HEIGHT = 1136;
const MIN_SCALE = 0.5; // zoom max vers l’extérieur
const MAX_SCALE = 2.5; // zoom max vers l’intérieur

// Chemin vers ton image (mets-la dans /public/images ou /src/assets selon ton setup)
const IMAGE_SRC = './images/jeu/frise-msd.webp';
const HALO_SRC = './images/jeu/lumiere.webp';
const PLANE_SRC = './images/jeu/premier-plan.webp';

// Contexts
import { useTimer } from '../contextes/useTimer';

// Components
import MissionHeader from '../components/MissionHeader';

// Fonctions
import { handleAddPenalty } from '../Fonctions';

// Page MicrobeScene pour afficher une grande image interactive
const MicrobeScene: React.FC = () => {
    const viewportRef = useRef<HTMLDivElement | null>(null);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const navigate = useNavigate();
    const { missionNumber } = useParams<{ missionNumber: string }>();
    const [scale, setScale] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [imageSize, setImageSize] = useState({ width: BASE_WIDTH, height: BASE_HEIGHT });
    const [isDragging, setIsDragging] = useState(false);
    const { removeTime } = useTimer();
    const [isPenaltyAdded, setIsPenaltyAdded] = useState(false);
    const [isPenaltyPressed, setIsPenaltyPressed] = useState(false);
    const [baseSize, setBaseSize] = useState({ width: BASE_WIDTH, height: BASE_HEIGHT });
    const [showInstruction, setShowInstruction] = useState(true);
    // Pour mémoriser l'état du pinch
    const pinchRef = useRef({
        isPinching: false,
        startDistance: 0,
        startScale: 1,
        centerX: 0,
        centerY: 0,
    });
    // Pour mémoriser la position de départ du drag
    const dragRef = useRef({
        startX: 0,
        startY: 0,
        startOffsetX: 0,
        startOffsetY: 0,
    });

    useEffect(() => {
        console.log(imageSize);
    }, [imageSize]);

    // Fonction appelée lorsque l'image est chargée pour recuperer ses dimensions
    const handleImageLoad = () => {
        if (!imgRef.current || !viewportRef.current) return;

        const vw = viewportRef.current.clientWidth;
        const vh = viewportRef.current.clientHeight;

        const iw = imgRef.current.naturalWidth;
        const ih = imgRef.current.naturalHeight;

        let initialScale = 1;

        // On scale seulement si l'image est plus petite que le viewport
        if (iw < vw || ih < vh) {
            initialScale = Math.max(vw / iw, vh / ih);
        }

        setBaseSize({ width: iw, height: ih });
        setScale(initialScale);

        const scaledWidth = iw * initialScale;
        const scaledHeight = ih * initialScale;

        setImageSize({ width: scaledWidth, height: scaledHeight });

        // centre l'image au départ
        const startX = (vw - scaledWidth) / 2;
        const startY = (vh - scaledHeight) / 2;
        setOffset({ x: startX, y: startY });
    };

    // Fonction pour clamp l'offset afin de ne pas dépasser les bords
    const clampOffset = (x: number, y: number, currentScale = scale) => {
        const vw = viewportRef.current?.clientWidth ?? 0;
        const vh = viewportRef.current?.clientHeight ?? 0;

        const width = baseSize.width * currentScale;
        const height = baseSize.height * currentScale;

        const maxX = 0;
        const maxY = 0;
        const minX = vw - width;
        const minY = vh - height;

        return {
            x: Math.max(minX, Math.min(maxX, x)),
            y: Math.max(minY, Math.min(maxY, y)),
        };
    };

    // Touche posée
    const handleTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
        const touches = e.touches;

        if (touches.length === 1) {
            const touch = touches[0];
            dragRef.current = {
                startX: touch.clientX,
                startY: touch.clientY,
                startOffsetX: offset.x,
                startOffsetY: offset.y,
            };
            setIsDragging(true);
            pinchRef.current.isPinching = false;
        } else if (touches.length === 2) {
            const t1 = touches[0];
            const t2 = touches[1];
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            const distance = Math.hypot(dx, dy);

            const centerX = (t1.clientX + t2.clientX) / 2;
            const centerY = (t1.clientY + t2.clientY) / 2;

            pinchRef.current = {
                isPinching: true,
                startDistance: distance,
                startScale: scale,
                centerX,
                centerY,
            };
            setIsDragging(false);
        }
    };

    // on glisse
    const handleTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
        if (showInstruction) setShowInstruction(false);
        const touches = e.touches;
        if (pinchRef.current.isPinching && touches.length === 2) {
            e.preventDefault();

            const t1 = touches[0];
            const t2 = touches[1];
            const dx = t2.clientX - t1.clientX;
            const dy = t2.clientY - t1.clientY;
            const distance = Math.hypot(dx, dy);

            if (pinchRef.current.startDistance === 0) return;

            let newScale =
                (distance / pinchRef.current.startDistance) * pinchRef.current.startScale;

            // clamp du zoom
            newScale = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));

            // Zoom autour du centre des doigts
            const { centerX, centerY } = pinchRef.current;

            // point de l'image sous les doigts avant zoom
            const relX = (centerX - offset.x) / scale;
            const relY = (centerY - offset.y) / scale;

            // nouvel offset pour garder ce point sous les doigts
            let newX = centerX - relX * newScale;
            let newY = centerY - relY * newScale;

            // clamp avec le nouveau scale
            const clamped = clampOffset(newX, newY, newScale);

            setScale(newScale);
            setImageSize({
                width: baseSize.width * newScale,
                height: baseSize.height * newScale,
            });
            setOffset(clamped);

            return;
        }

        // Pan classique à un doigt
        if (!isDragging || touches.length !== 1) return;
        const touch = touches[0];

        const dx = touch.clientX - dragRef.current.startX;
        const dy = touch.clientY - dragRef.current.startY;

        const newX = dragRef.current.startOffsetX + dx;
        const newY = dragRef.current.startOffsetY + dy;

        setOffset(clampOffset(newX, newY));
    };

    // on relâche
    const handleTouchEnd: React.TouchEventHandler<HTMLDivElement> = (e) => {
        if (e.touches.length === 0) {
            setIsDragging(false);
            pinchRef.current.isPinching = false;
        }
    };

    // Empêche le bouton back de fonctionner sur cette page
    useEffect(() => {
        // Empêche le retour arrière en ajoutant une entrée dans l'historique
        window.history.pushState(null, '', window.location.href);

        const guard = () => {
            // Annule le back et re-push
            window.history.pushState(null, '', window.location.href);
        };

        window.addEventListener('popstate', guard);
        return () => window.removeEventListener('popstate', guard);
    }, []);

    return (
        <div className='microbe-page'>
            <div className='microbe-header'>
                <MissionHeader
                    showBackButton={false}
                    showSettingsButton={false}
                    showCloseButton={true}
                    logoVariant='dark'
                    closeFunction={() => navigate(`/mission/${missionNumber}/game`)}
                    showTimer={true}
                    timerVariant='sombre'
                />
            </div>
            <div
                className={`microbe-viewport ${isDragging ? 'is-dragging' : ''}`}
                ref={viewportRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onTouchCancel={handleTouchEnd}
            >
                {/* Image géante */}
                <img
                    ref={imgRef}
                    src={IMAGE_SRC}
                    alt='Microbes'
                    className='microbe-image'
                    onLoad={handleImageLoad}
                    style={{
                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                        transformOrigin: 'top left',
                    }}
                />
                <img
                    src={HALO_SRC}
                    alt='Halo'
                    className='halo-image'
                />
                <img
                    src={PLANE_SRC}
                    alt='Microbes'
                    className='microbe-image'
                    onLoad={handleImageLoad}
                    style={{
                        transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                        transformOrigin: 'top left',
                    }}
                />
            </div>
            {showInstruction && (
                <p className='microbe-instru'>Glissez votre doigt sur l'écran pour vous déplacer</p>
            )}
            <div className='microbe-buttons'>
                <div className='microbe-button_wrap'>
                    <div className='microbe-button-shadow-red'></div>

                    <button
                        className={`microbe-btn microbe-btn--red ${
                            isPenaltyPressed ? 'is-pressed' : ''
                        }`}
                        onClick={() => handleAddPenalty(removeTime, setIsPenaltyAdded)}
                        onPointerDown={() => setIsPenaltyPressed(true)}
                        onPointerUp={() => setIsPenaltyPressed(false)}
                        onPointerLeave={() => setIsPenaltyPressed(false)}
                        onPointerCancel={() => setIsPenaltyPressed(false)}
                    >
                        <p>Pénalité</p>
                        <img
                            src='./images/penalty-image.png'
                            alt='Penalité'
                            className='microbe-button-icon'
                        />
                    </button>
                </div>
                {isPenaltyAdded && <div className='penalty-notification'>−5 sec</div>}
            </div>
        </div>
    );
};

export default MicrobeScene;
